import { useRef } from 'react';
import { useLoaderData } from 'react-router';

import {
    DetailMobileAppChrome,
    EditorialClosing,
    useIslandHideNearBottom,
} from '~/ui-rocco/pages/detail-shared';
import { GetMatchedBand } from '~/ui-rocco/pages/details-page/revamp/sections/GetMatchedBand';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { BookMeetingAccordionProvider } from './hooks/use-book-meeting-accordion';
import { ProjectDetailsMagicIsland } from './magic-island/ProjectDetailsMagicIsland';
import { ProjectHeroSection } from './sections/ProjectHeroSection';
import { ProjectSummarySection } from './sections/ProjectSummarySection';
import { ProjectCreditsRow } from './sections/ProjectCreditsRow';
import { ProjectStorySection } from './sections/ProjectStorySection';
import { ProjectAccordionSection } from './sections/ProjectAccordionSection';
import { ProjectGallerySection } from './sections/ProjectGallerySection';
import { AboutProfessionalSection } from './sections/AboutProfessionalSection';
import { RelatedProjectsSection } from './sections/RelatedProjectsSection';
import { SimilarProjectsSection } from './sections/SimilarProjectsSection';
import { ProjectEnquiryPrompt } from './sections/ProjectEnquiryPrompt';

/**
 * Revamped editorial project details page.
 *
 * Built section-by-section (P2–P10) per
 * `plans/BC-4505-details-page-revamp.md` → "Project details — section breakdown".
 * Sections are added incrementally, top to bottom; the loader is shared with the
 * legacy page and stays unchanged. Rendered in place of the legacy page behind
 * the `details_revamp_2026` feature flag (or `?revamp=1` in local dev).
 */
export const ProjectPageRevamp = () => {
    const { project } = useLoaderData<typeof loader>();
    // The featured editorial (ProjectStorySection) is optional. Only widen the
    // lead-in to the design's L gap when it will actually render — otherwise the
    // credits → accordion gap stays at the standard spacing (no empty band).
    const hasFeaturedArticle = Boolean(
        (
            project as typeof project & {
                FeaturedArticle?: { Link: string } | null;
            }
        ).FeaturedArticle,
    );

    return (
        <BookMeetingAccordionProvider>
            <ProjectDetailsMagicIsland>
                <DetailMobileAppChrome />
                <main
                    id="main-content"
                    data-testid="project-page-revamp"
                    // overflow-x-clip contains the gallery modules' 4px full-bleed
                    // so it never triggers a horizontal scrollbar (it clips
                    // without becoming a scroll container, so the fixed island and
                    // vertical scroll are unaffected).
                    className="overflow-x-clip bg-[#181818]"
                >
                    {/* Sections P2–P10 land here, top to bottom. The content block
                    sits on the dark editorial background; the hero is a
                    full-bleed image and the closing brings its own sections. */}
                    <ProjectHeroSection />
                    <SectionSpacer height={48} mobileHeight={24} unit="rem" />
                    <ProjectSummarySection />
                    <SectionSpacer height={64} mobileHeight={24} unit="rem" />
                    <ProjectCreditsRow />
                    {/* Generous L lead-in to the featured editorial — only when
                        that (optional) section renders; otherwise the standard
                        64px gap to the accordion. */}
                    <SectionSpacer
                        height={hasFeaturedArticle ? 160 : 64}
                        mobileHeight={hasFeaturedArticle ? 59 : 32}
                        unit="rem"
                    />
                    <ProjectStorySection />
                    <SectionSpacer height={56} mobileHeight={28} unit="rem" />
                    <ProjectAccordionSection />
                    <SectionSpacer height={80} mobileHeight={40} unit="rem" />
                    <ProjectGallerySection />
                    {/* End-of-gallery enquiry prompt — Book a Meeting + the
                        website referral, rendered where the images have just
                        made the case (the legacy banner's slot, now
                        enquiry-led on both devices). */}
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    <ProjectEnquiryPrompt
                        heading="Planning something similar?"
                        body="Book a meeting with {supplier} to talk through your project."
                    />
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    <AboutProfessionalSection />
                    {/* Mobile folds the related carousel into About-the-
                        Professional, so this gap collapses there (the next
                        spacer carries the gap down to Similar Projects). */}
                    <SectionSpacer height={120} mobileHeight={0} unit="rem" />
                    <RelatedProjectsSection />
                    <SectionSpacer height={120} mobileHeight={48} unit="rem" />
                    <SimilarProjectsSection />
                    <SectionSpacer height={160} mobileHeight={59} unit="rem" />
                    <ProjectClosing />
                </main>
            </ProjectDetailsMagicIsland>
        </BookMeetingAccordionProvider>
    );
};

/**
 * The page's closing region: the Get Matched module over the shared editorial
 * closing — a reader who passed every enquiry prompt without acting probably
 * won't enquire with this professional, so the exit offers describing the
 * project once and letting matching professionals come to them. Wrapped in a
 * ref so the island hides once the closing reaches the island's berth (no
 * pill over the footer).
 */
const ProjectClosing = () => {
    const { getMatchedStyleImages } = useLoaderData<typeof loader>();
    const ref = useRef<HTMLDivElement>(null);
    useIslandHideNearBottom(ref, true);

    return (
        // bg-white backs the whole closing so the shared editorial blocks (built
        // for the white homepage) never let the page's dark #181818 show through
        // the sub-pixel seams between their fixed-aspect sections as stray lines.
        <div ref={ref} className="bg-white">
            <SectionSpacer
                height={80}
                mobileHeight={48}
                className="bg-white"
                unit="rem"
            />
            <GetMatchedBand
                enquiryType="project"
                styleImages={getMatchedStyleImages}
            />
            <SectionSpacer
                height={80}
                mobileHeight={59}
                className="bg-white"
                unit="rem"
            />
            <EditorialClosing />
        </div>
    );
};
